import axios, { AxiosError, AxiosResponse } from "axios";

import { IPortalPreferences, IUserPreferences } from "../@types/Preferences";
import {
  IConfirmEnableMfa,
  IMfa,
  IUpdatePortalPreferences,
  IUpdateUserUnitPreferences,
  ICurrentUser,
} from "../@types/User";
import { api } from "./api";

export const userService = {
  async getCurrentUser(): Promise<ICurrentUser> {
    const url = "/Portal/Users/Me";

    const serverResponse = await api
      .get(url)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response?.data;
        }
        return response.data;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async disableUserMfa(): Promise<AxiosResponse> {
    const url = `/Portal/Authentication/DisableMfa`;

    const serverResponse = await api
      .post(url)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async enableUserMfa({
    email,
    phoneNumber,
    mfaType,
  }: IMfa): Promise<AxiosResponse> {
    const url = "/Portal/Authentication/EnableMfa";

    const serverResponse = await api
      .post(url, {
        email,
        phoneNumber,
        mfaType,
      })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async confirmEnableUserMfa({
    email,
    code,
    mfaType,
  }: IConfirmEnableMfa): Promise<AxiosResponse> {
    const url = "/Portal/Authentication/ConfirmEnableMfa";

    const serverResponse = await api
      .post(url, { email, code, mfaType })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async resendOtp({
    email,
    mfaType,
    phoneNumber,
  }: IMfa): Promise<AxiosResponse> {
    const url = "/Portal/Authentication/ResendOtp";

    const serverResponse = await api
      .post(url, {
        email,
        mfaType,
        phoneNumber,
      })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async confirmMfaOtp({ email, code }: any): Promise<AxiosResponse> {
    const url = "/Portal/Authentication/ConfirmEnableMfa";

    const serverResponse = await api
      .post(url, { email, code })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateUserDataDisplayPreference(
    dataDisplayConfig: string
  ): Promise<AxiosResponse> {
    const url = "/Legacy/UserPreferences/UpdateDataDisplayPreference";

    const serverResponse = await api
      .post(url, { dataDisplayConfig })
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateTrendLinePreferences(
    measurement: string,
    displayTrendLine: boolean
  ): Promise<AxiosResponse> {
    const url = `/Portal/Users/UpdateTrendLine?Measurement=${measurement}&DisplayTrendLine=${displayTrendLine}`;

    const serverResponse = await api
      .put(url)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async getUserPreferences(): Promise<AxiosResponse<IUserPreferences>> {
    const url = "/Portal/Users/GetUserPreferences";

    const serverResponse = await api
      .get(url)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateUserUnitPreferences(
    userUnitPrefences: IUpdateUserUnitPreferences
  ): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateUserUnitPreferences";

    const serverResponse = await api.post(url, userUnitPrefences);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  /**
   * EN-5191: sets the signed-in user's display language.
   *
   * Its own endpoint rather than a field on UpdateUserUnitPreferences, which assigns every unit
   * from its request body — folding the language in there would blank it every time someone
   * saved their units from this same Settings page.
   */
  async updateUserLanguage(languageCode: string): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateUserLanguage";

    const serverResponse = await api.post(url, { languageCode });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async getPortalPreferences(): Promise<AxiosResponse<IPortalPreferences>> {
    const url = "/Portal/Users/GetPortalPreferences";

    const serverResponse = await api
      .get(url)
      .then((response: AxiosResponse | AxiosError) => {
        if (axios.isAxiosError(response)) {
          return response.response;
        }
        return response;
      });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateBillingTableColumnPreferences(
    billingTableColumnPreferences: string
  ): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateBillingTableColumnPreferences";

    const serverResponse = await api.put(url, {
      billingTableColumnPreferences,
    });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateAlertsTableColumnPreferences(
    alertsTableColumnPreferences: string
  ): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateAlertsTableColumnPreferences";

    const serverResponse = await api.put(url, {
      alertsTableColumnPreferences,
    });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updatePatientListConfig(
    userPortalPrefences: IUpdatePortalPreferences
  ): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdatePatientListConfig";

    const serverResponse = await api.put(url, userPortalPrefences);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateWardsConfig(wardsConfig: number[]): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateWardsConfig";

    const serverResponse = await api.put(url, wardsConfig);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateScaleYAxis(scaleYAxis: boolean): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateScaleYAxisConfig";

    const serverResponse = await api.put(url, scaleYAxis);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateCollapsedSectionsConfig(
    collapsedSectionsConfig: string
  ): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateCollapsedSectionsConfig";

    const serverResponse = await api.put(url, { collapsedSectionsConfig });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updatePinnedMeasurementType(
    measurementType?: string
  ): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdatePinnedMeasurementType";

    const serverResponse = await api.put(url, {
      pinnedMeasurementType: measurementType,
    });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateSelectedViewType(viewType: string): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateSelectedViewType";

    const serverResponse = await api.put(url, {
      selectedViewType: viewType,
    });

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },

  async updateDateRangeFilter(dateRangeFilter: string): Promise<AxiosResponse> {
    const url = "/Portal/Users/UpdateDateRangeFilter";

    const serverResponse = await api.put(url, dateRangeFilter);

    if (serverResponse !== undefined) {
      return serverResponse;
    }

    throw Error;
  },
};
